import { Lissajous2 } from "@components/Lissajous";
import Button from "components/library/Button";
import {
  ActiveLearningLogo,
  AutoTrainLogo,
  HumanInTheLoopDeploymentLogo,
  InferenceLogo,
  ProgrammaticLogo,
} from "components/ProductsLogos";
import { SectionBadge } from "components/SectionBadge";
import { useBreakpoints } from "hooks/useBreakpoints";
import { HUMANLOOP_DOCS_LINK } from "utils/constants";
import { ArrowUpRightIcon, ShapeCubeIcon } from "../library/Icons";
import { CTASmall } from "./CTASmall";
import { FeatureCard } from "./FeatureCard";
import { Quote } from "./Quote";
import { SectionTitleAndDescription } from "./SectionTitleAndDescription";

export const DeploySection = () => {
  const { isMediumWidth } = useBreakpoints();

  return (
    <div className="bg-gray-100 dark:bg-black">
      <section className="container-xl">
        <div className="relative py-48 md:pt-160">
          <div className="mx-12 mb-64 md:mx-[64px]">
            <div className="mb-12 -ml-4 md:mb-20">
              <SectionBadge>
                <Lissajous2 delay={-90} />
                Deploy
              </SectionBadge>
            </div>
            <SectionTitleAndDescription
              title="Don’t wait for the perfect model to deploy"
              description={
                <>
                  Deploy models quickly with confidence. <br />
                  <br />
                  Models train as your label and are instantly deployed to our
                  inference API. Our human-in-the-loop APIs guarantee
                  performance and get you to market faster.
                </>
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-16">
            <FeatureCard
              wide
              title={
                <HumanInTheLoopDeploymentLogo size={isMediumWidth ? 24 : 16} />
              }
              description={
                // "Easily deploy models to production while steadily improving them over time."
                "Ensure quality predictions with imperfect models by setting up human review when the model is unsure."
              }
              callouts={[
                {
                  title: "Get to market quicker",
                  description:
                    "Provide the desired experience without the months of data science",
                },
                {
                  title: "Automates over time",
                  description:
                    "The model learns from every intervention and will retrain as needed.",
                },
                {
                  title: "Guarantee performance",
                  description:
                    "Enable human review for mission-critical models",
                },

                {
                  title: "Bring your own model",
                  description:
                    "Drive our interfaces with your own model or use our powerful pre-trained language model.",
                },
              ]}
              learnMore={
                <Button
                  href={HUMANLOOP_DOCS_LINK}
                  styling="ghost"
                  className="-ml-12"
                  IconRight={ArrowUpRightIcon}
                >
                  Read the docs
                </Button>
              }
              image={
                <div className="mx-16">
                  <img
                    className="mx-auto h-full rounded-ms shadow-c dark:shadow-none md:mt-20"
                    src="/assets/product/hitl-deployment.svg"
                    alt="Programmatic"
                  />
                </div>
              }
            />

            <FeatureCard
              title={<AutoTrainLogo size={isMediumWidth ? 24 : 16} />}
              description={
                "Get a model as quickly as possible and keep it up to date with our automatic retraining process."
                // Automatically train state-of-the-art models with AutoTrain or configure your own model."
              }
              callouts={[
                {
                  title: "No-code model training",
                  description:
                    "Simply by annotating your data, your model stays up to date.",
                },
              ]}
              image={
                <img
                  className=" mx-auto -mt-20 max-h-[180px] w-full rounded-ms md:mx-0 md:h-[173px] "
                  src="/assets/product/continuous-retraining-sm-dark.svg"
                  alt="Continuous retraining"
                />
              }
            />

            <FeatureCard
              title={<InferenceLogo size={isMediumWidth ? 24 : 16} />}
              description={
                "Easily deploy models with our autoscaling GPU cluser, so you can focus on your business."
              }
              callouts={[
                {
                  title: "Painfree deployment",
                  description:
                    "Scales as you need, pay only for what you use. Monitoring built in.",
                },
              ]}
              image={
                <img
                  className="mx-auto max-h-[240px] w-4/5 rounded-ms text-left md:mx-0 md:h-[180px]"
                  src="/assets/product/model-api.svg"
                  alt="Model API response"
                />
              }
            />
          </div>
          <div className="my-[104px] grid grid-cols-1 gap-y-48 md:grid-cols-2">
            <CTASmall text="Annotate data and train a model in minutes" />
            <div className="md:pl-32">
              <Quote
                // quote="Humanloop has been really transformative for us. In just hours we’ve been able to train a really high performing model. Without Humanloop this would have taken us weeks. We’ve experimented with OpenAI’s GPT-3 API and we just couldn’t get as reliable results. With any other tool it would have taken us this long just to get labeled data. With Humanloop we got a great model and deployed it in that time."
                quote="We’ve experimented with OpenAI’s GPT-3 API and we just couldn’t get reliable results. With Humanloop we got a great model and deployed it in the time it would have taken us just to get labeled data with any other tool. "
                name="Neel Patel"
                company="Head of Product at Datanautix"
                image="/assets/customers/neel-datanautix.png"
                products={[AutoTrainLogo, InferenceLogo]}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
